import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  schema?: Record<string, unknown>[];
}

export default function SEOHead({ data }: { data: SEOData }) {
  const location = useLocation();
  const canonical = data.canonical || `https://tianbaolaowu.cn${location.pathname}`;

  useEffect(() => {
    document.title = data.title;
    updateMeta('description', data.description);
    updateMeta('keywords', data.keywords);
    updateLink('canonical', canonical);
    updateMeta('og:title', data.title);
    updateMeta('og:description', data.description);
    updateMeta('og:url', canonical);
    updateMeta('og:type', data.ogType || 'website');
    if (data.ogImage) updateMeta('og:image', data.ogImage);
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', data.title);
    updateMeta('twitter:description', data.description);
    if (data.ogImage) updateMeta('twitter:image', data.ogImage);

    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((s) => s.remove());

    if (data.schema) {
      data.schema.forEach((schemaObj) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schemaObj);
        document.head.appendChild(script);
      });
    }

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach((s) => s.remove());
    };
  }, [data, canonical]);

  return null;
}

function updateMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    if (name.startsWith('og:') || name.startsWith('twitter:')) {
      el.setAttribute('property', name);
    } else {
      el.setAttribute('name', name);
    }
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function updateLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  (el as HTMLLinkElement).href = href;
}